import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Calendar, Briefcase, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const ExperienceStep = ({ formData, updateFormData, onNext, onPrev, onSkip }) => {
  const [experiences, setExperiences] = useState(formData.experience || []);
  const [showForm, setShowForm] = useState(false);
  const [currentExperience, setCurrentExperience] = useState({
    title: '',
    company: '',
    location: '',
    startDate: '',
    endDate: '',
    current: false,
    description: ''
  });
  const [editIndex, setEditIndex] = useState(-1);
  const [errors, setErrors] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(-1);
  
  // Check if any experiences are from parsed resume
  const [hasParsedData, setHasParsedData] = useState(false);
  
  useEffect(() => {
    // Check if we have parsed resume data
    if (formData.resume && formData.resume.parsed && formData.resume.parsedData?.experience) {
      // Only set if we don't already have experiences
      if (experiences.length === 0) {
        setExperiences(formData.resume.parsedData.experience);
        setHasParsedData(true);
      }
    }
  }, [formData.resume, experiences.length]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
      return dateString;
    }
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentExperience(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCurrentExperience(prev => ({
      ...prev,
      [name]: checked,
      // If current job, clear end date
      ...(name === 'current' && checked ? { endDate: '' } : {})
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!currentExperience.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!currentExperience.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    
    if (!currentExperience.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!currentExperience.current && !currentExperience.endDate) {
      newErrors.endDate = 'End date is required if not current job';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Add or update experience
  const handleSaveExperience = () => {
    if (!validateForm()) return;
    
    if (editIndex >= 0) {
      // Update existing experience
      const updatedExperiences = [...experiences];
      updatedExperiences[editIndex] = currentExperience;
      setExperiences(updatedExperiences);
    } else {
      // Add new experience
      setExperiences(prev => [...prev, currentExperience]);
    }
    
    // Reset form
    setCurrentExperience({
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: ''
    });
    setEditIndex(-1);
    setShowForm(false);
    setHasParsedData(false); // No longer using just parsed data
  };
  
  // Edit experience
  const handleEditExperience = (index) => {
    setCurrentExperience(experiences[index]);
    setEditIndex(index);
    setShowForm(true);
    setExpandedIndex(-1); // Close any expanded view
  };
  
  // Delete experience
  const handleDeleteExperience = (index) => {
    const updatedExperiences = experiences.filter((_, i) => i !== index);
    setExperiences(updatedExperiences);
    setHasParsedData(false); // No longer using just parsed data
  };
  
  // Toggle expanded view
  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };
  
  // Handle continue
  const handleContinue = () => {
    updateFormData('experience', experiences);
    onNext();
  };
  
  // Calculate total years of experience
  const calculateTotalExperience = () => {
    if (experiences.length === 0) return 0;
    
    let totalMonths = 0;
    
    experiences.forEach(exp => {
      const startDate = new Date(exp.startDate);
      const endDate = exp.current ? new Date() : new Date(exp.endDate);
      
      if (isNaN(startDate) || isNaN(endDate)) return;
      
      const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                     (endDate.getMonth() - startDate.getMonth());
      
      totalMonths += months > 0 ? months : 0;
    });
    
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    
    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Add Your Work Experience 💼</h1>
        <p className="text-gray-600 text-center mb-8">
          Tell employers about your work history and professional journey
        </p>
        
        {/* Experience list */}
        {experiences.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                Your Experience ({experiences.length})
                {experiences.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    {calculateTotalExperience()} total
                  </span>
                )}
              </h2>
              {hasParsedData && (
                <div className="text-sm text-blue-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Added from your resume
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {experiences.map((experience, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-800">{experience.title}</h3>
                        <p className="text-gray-600">{experience.company}</p>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Calendar size={14} className="mr-1" />
                          <span>
                            {formatDate(experience.startDate)} - {experience.current ? 'Present' : formatDate(experience.endDate)}
                          </span>
                          {experience.location && (
                            <>
                              <span className="mx-2">•</span>
                              <MapPin size={14} className="mr-1" />
                              <span>{experience.location}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-2">
                        <button
                          type="button"
                          onClick={() => handleEditExperience(index)}
                          className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteExperience(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(index)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {expandedIndex === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded description */}
                    <AnimatePresence>
                      {expandedIndex === index && experience.description && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{experience.description}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Add experience button or form */}
        {!showForm ? (
          <motion.button
            type="button"
            className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center mb-8"
            onClick={() => setShowForm(true)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Plus size={18} className="mr-2" />
            Add Work Experience
          </motion.button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm"
          >
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              {editIndex >= 0 ? 'Edit Experience' : 'Add Experience'}
            </h2>
            
            <div className="space-y-4">
              {/* Job Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Job Title*
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={currentExperience.title}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${errors.title ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    placeholder="e.g. Software Engineer"
                  />
                </div>
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>
              
              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company*
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={currentExperience.company}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-2 border ${errors.company ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    placeholder="e.g. Acme Corporation"
                  />
                </div>
                {errors.company && <p className="mt-1 text-sm text-red-600">{errors.company}</p>}
              </div>
              
              {/* Location */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Location <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={currentExperience.location}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g. Mumbai, India"
                  />
                </div>
              </div>
              
              {/* Employment Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employment Period*
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-xs text-gray-500 mb-1">
                      Start Date
                    </label>
                    <input
                      type="month"
                      id="startDate"
                      name="startDate"
                      value={currentExperience.startDate}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.startDate ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    />
                    {errors.startDate && <p className="mt-1 text-xs text-red-600">{errors.startDate}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="endDate" className="block text-xs text-gray-500 mb-1">
                      End Date
                    </label>
                    <input
                      type="month"
                      id="endDate"
                      name="endDate"
                      value={currentExperience.endDate}
                      onChange={handleInputChange}
                      disabled={currentExperience.current}
                      className={`w-full px-3 py-2 border ${errors.endDate ? 'border-red-300' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${currentExperience.current ? 'bg-gray-100' : ''}`}
                    />
                    {errors.endDate && <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>}
                  </div>
                </div>
                
                <div className="mt-2">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      name="current"
                      checked={currentExperience.current}
                      onChange={handleCheckboxChange}
                      className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-400"
                    />
                    <span className="ml-2 text-sm text-gray-700">I currently work here</span>
                  </label>
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={currentExperience.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Describe your responsibilities and achievements..."
                ></textarea>
              </div>
              
              {/* Form buttons */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setShowForm(false);
                    setCurrentExperience({
                      title: '',
                      company: '',
                      location: '',
                      startDate: '',
                      endDate: '',
                      current: false,
                      description: ''
                    });
                    setEditIndex(-1);
                    setErrors({});
                  }}
                >
                  Cancel
                </button>
                
                <motion.button
                  type="button"
                  className="px-4 py-2 bg-black text-white font-medium rounded-md hover:bg-gray-800 transition-colors"
                  onClick={handleSaveExperience}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {editIndex >= 0 ? 'Update' : 'Save'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Tips */}
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-8">
          <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Pro Tips
          </h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-6 list-disc">
            <li>Include your most recent and relevant work experiences</li>
            <li>Quantify your achievements with numbers when possible</li>
            <li>Use action verbs to describe your responsibilities</li>
            <li>Focus on achievements rather than just duties</li>
          </ul>
        </div>
        
        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={() => {
                updateFormData('experience', experiences);
                onSkip();
              }}
            >
              Skip for now
            </button>
            
            <motion.button
              type="button"
              className="px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all"
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ExperienceStep;
